import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { ShoppingBag, User, Menu, X, LayoutDashboard, LogOut, Package } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const LINKS = [
  { label: "Shop All", to: "/shop" },
  { label: "Hoodies", to: "/shop?category=Hoodies" },
  { label: "Tees", to: "/shop?category=Oversized+Tees" },
  { label: "Cargos", to: "/shop?category=Cargo+Pants" },
  { label: "Outerwear", to: "/shop?category=Outerwear" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { count, setOpen } = useCart();
  const navigate = useNavigate();
  const [mobile, setMobile] = useState(false);

  return (
    <>
      <div className="w-full overflow-hidden bg-brand text-black">
        <div className="flex whitespace-nowrap animate-marquee font-mono text-[11px] font-semibold uppercase tracking-widest py-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex shrink-0">
              {Array.from({ length: 6 }).map((__, j) => (
                <span key={j} className="mx-6">DROP 004 IS LIVE — FREE SHIPPING OVER ₹2,999 — UPI ACCEPTED</span>
              ))}
            </div>
          ))}
        </div>
      </div>

      <header className="sticky top-0 z-40 border-b border-zinc-800 bg-zinc-950/80 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex h-16 items-center justify-between">
            <Link to="/" data-testid="nav-logo" className="font-display text-2xl font-extrabold uppercase tracking-tight">
              laze<span className="text-brand">.</span>
            </Link>

            <nav className="hidden lg:flex items-center gap-8">
              {LINKS.map((l) => (
                <Link
                  key={l.label}
                  to={l.to}
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="font-mono text-xs uppercase tracking-widest text-zinc-400 hover:text-brand transition-colors"
                >
                  {l.label}
                </Link>
              ))}
            </nav>

            <div className="flex items-center gap-2">
              <button
                data-testid="nav-cart-btn"
                onClick={() => setOpen(true)}
                className="relative p-2 hover:text-brand transition-colors"
                aria-label="Cart"
              >
                <ShoppingBag className="h-5 w-5" />
                {count > 0 && (
                  <span
                    data-testid="nav-cart-count"
                    className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-brand text-black text-[10px] font-bold flex items-center justify-center"
                  >
                    {count}
                  </span>
                )}
              </button>

              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button data-testid="nav-user-menu" className="p-2 hover:text-brand transition-colors" aria-label="Account">
                      <User className="h-5 w-5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 bg-zinc-900 border-zinc-800">
                    <div className="px-2 py-1.5 text-xs text-zinc-400 truncate font-mono">{user.email}</div>
                    <DropdownMenuSeparator className="bg-zinc-800" />
                    <DropdownMenuItem data-testid="menu-orders" onClick={() => navigate("/account")} className="cursor-pointer">
                      <Package className="mr-2 h-4 w-4" /> My Orders
                    </DropdownMenuItem>
                    {user.role === "admin" && (
                      <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")} className="cursor-pointer">
                        <LayoutDashboard className="mr-2 h-4 w-4" /> Admin Dashboard
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator className="bg-zinc-800" />
                    <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); navigate("/"); }} className="cursor-pointer text-red-400">
                      <LogOut className="mr-2 h-4 w-4" /> Log out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  to="/login"
                  data-testid="nav-login-btn"
                  className="hidden sm:inline-flex items-center rounded-full bg-white px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-black hover:bg-brand transition-colors active:scale-95"
                >
                  Login
                </Link>
              )}

              <button className="lg:hidden p-2" onClick={() => setMobile((m) => !m)} data-testid="nav-mobile-toggle" aria-label="Menu">
                {mobile ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>

        {mobile && (
          <div className="lg:hidden border-t border-zinc-800 bg-zinc-950 px-4 py-4 space-y-3">
            {LINKS.map((l) => (
              <Link
                key={l.label}
                to={l.to}
                onClick={() => setMobile(false)}
                className="block font-mono text-sm uppercase tracking-widest text-zinc-300"
              >
                {l.label}
              </Link>
            ))}
            {!user && (
              <Link to="/login" onClick={() => setMobile(false)} className="block font-mono text-sm uppercase tracking-widest text-brand">
                Login / Signup
              </Link>
            )}
          </div>
        )}
      </header>
    </>
  );
}
